import { ApiErrorCodeLabelModifier } from '@/api/error-codes';
import {
  AutoCompleteField,
  type AutoCompleteOptionType,
  Box,
  Button,
  Flex,
  Radio,
  Text,
} from '@/components';
import { containHalfWidthString } from '@/utils';
import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import type { AxiosError } from 'axios';
import isEqual from 'lodash-es/isEqual';
import omitBy from 'lodash-es/omitBy';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type {
  CorporatesModelType,
  GovernmentSearchSettingFormValues,
  SearchTypeEnum,
} from '../_models/government-search';
import {
  fetchGovernment,
  fetchGovernmentNumber,
} from '../_utils/search-government';
import { PAYMENT_SEARCH_SETTING_DEFAULT_VALUES } from '../_utils/search-government';
import type { GovernmentSearchFieldProps } from './GovernmentSearchField';

type SearchResult =
  | {
      status: 'loading' | 'waiting';
    }
  | {
      errorMessage: string;
      status: 'failed';
    };

type SearchType = z.infer<typeof SearchTypeEnum>;

type Props<T extends FieldValues, U extends AutoCompleteOptionType> = {
  onOpenModal: () => void;
  searchSetting: GovernmentSearchSettingFormValues;
  onChangeSearchType?: (searchType: SearchType) => void;
  companyNameHint?: string;
  companyNumberHint?: string;
} & GovernmentSearchFieldProps<T, U>;

export const SearchWordField = <
  T extends FieldValues,
  U extends AutoCompleteOptionType,
>({
  onSelect,
  onChange,
  onOpenModal,
  onChangeSearchType,
  searchSetting,
  searchValue,
  defaultSearchValue,
  isResetProcessing,
  companyNameHint,
  companyNumberHint,
  ...restProps
}: Props<T, U>) => {
  const [searchWord, setSearchWord] = useState<string | undefined>(
    defaultSearchValue,
  );
  const [searchType, setSearchType] = useState<SearchType>('companyName');
  const { setValue } = useFormContext<any>();

  const [corporateResult, setCorporateResult] = useState<CorporatesModelType[]>(
    [],
  );
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const prevSearchSetting =
    useRef<GovernmentSearchSettingFormValues>(searchSetting);
  const [searchResult, setSearchResult] = useState<SearchResult>({
    status: 'waiting',
  });

  const handleSearch = useCallback(async () => {
    if (!searchWord) {
      setCorporateResult([]);
      return;
    }

    const isCompanyNameContainsHalfWidthCharacters =
      searchType === 'companyName' && !!containHalfWidthString(searchWord);

    const isCompanyNumberContainsNumber =
      searchType === 'companyNumber' &&
      (Number.isNaN(Number(searchWord)) || searchWord.length !== 13);

    if (
      isCompanyNameContainsHalfWidthCharacters ||
      isCompanyNumberContainsNumber
    ) {
      setSearchResult({
        status: 'failed',
        errorMessage:
          searchType === 'companyName'
            ? t('validation.disallow-half-width-characters-in-payment-name')
            : t('validation.disallow-half-width-characters-in-company-number'),
      });
      setCorporateResult([]);
      return;
    }

    setSearchResult({ status: 'loading' });

    try {
      const result =
        searchType === 'companyName'
          ? await fetchGovernment({
              searchString: searchWord,
              corporateKinds: searchSetting.corporateKind?.length
                ? searchSetting.corporateKind
                : null,
              prefectureCode: searchSetting.prefecture ?? null,
              historySearch:
                typeof searchSetting.includeHistorySearch === 'boolean'
                  ? searchSetting.includeHistorySearch
                  : null,
              excludeClosedCorporation:
                typeof searchSetting.includeClosedCorporation === 'boolean'
                  ? !searchSetting.includeClosedCorporation
                  : null,
              matchType: searchSetting.matchType,
            })
          : await fetchGovernmentNumber({
              corporateNumber: searchWord,
            });

      if (result.length === 0) {
        setSearchResult({
          status: 'failed',
          errorMessage: t('empty-result-corporate'),
        });
      } else {
        setSearchResult({ status: 'waiting' });
      }
      setCorporateResult(result);
    } catch (e) {
      setCorporateResult([]);
      const { response } = e as AxiosError<{ error_code: string }>;
      const errorMessageByErrorCode =
        ApiErrorCodeLabelModifier[response?.data.error_code ?? ''];

      setSearchResult({
        status: 'failed',
        errorMessage: errorMessageByErrorCode || t('failed-fetch-corporate'),
      });
    }
  }, [t, searchSetting, searchWord, searchType]);

  const onReset = useCallback(
    (searchType?: SearchType) => {
      setSearchWord(undefined);
      setSearchResult({ status: 'waiting' });
      setSearchType(searchType ?? 'companyName');
      setCorporateResult([]);
      setValue(restProps.name, null as any);
    },
    [restProps.name, setValue],
  );

  // reset親起因のreset処理
  useEffect(() => {
    if (isResetProcessing) {
      onReset();
    }
  }, [isResetProcessing, onReset]);

  useEffect(() => {
    // 前回の検索設定と違う場合は検索処理を走らせる。
    if (!isEqual(prevSearchSetting.current, searchSetting)) {
      (async () => {
        await handleSearch();
        prevSearchSetting.current = searchSetting;
        if (searchWord) {
          setDropdownOpen(true);
        }
      })();
    }
  }, [searchSetting, handleSearch, searchWord]);

  const isSettingChanged = useMemo(() => {
    return !isEqual(
      omitBy(searchSetting, (value) => value === null || value === undefined),
      omitBy(
        PAYMENT_SEARCH_SETTING_DEFAULT_VALUES,
        (value) => value === null || value === undefined,
      ),
    );
  }, [searchSetting]);

  const corporateOptions = useMemo(() => {
    return corporateResult.map((corporate) => {
      return {
        value: corporate.corporateNumber,
        label: (
          <Flex vertical>
            <Text
              variant="footnote"
              color="secondary"
              text={corporate.corporateNumber}
            />
            <Text text={corporate.corporateName} />
            <Text
              variant="footnote"
              color="secondary"
              text={corporate.corporateAddress}
            />
          </Flex>
        ),
      };
    });
  }, [corporateResult]);

  return (
    <>
      <Flex align="center" gap={8}>
        <AutoCompleteField
          open={dropdownOpen}
          searchValue={searchWord}
          onDropdownVisibleChange={setDropdownOpen}
          options={corporateOptions}
          onSearch={handleSearch}
          onSelect={(corporateNumber) => {
            const corporateOption = corporateResult.find(
              (corporate) => corporate.corporateNumber === corporateNumber,
            );
            onSelect({ corporateInfo: corporateOption });
            setSearchWord(corporateOption?.corporateName);
          }}
          onChange={(value) => {
            onChange?.(value);
            setSearchResult({ status: 'waiting' });
            setSearchWord(value);
          }}
          loading={searchResult.status === 'loading'}
          wrapperStyle={{
            marginBottom: searchResult.status === 'failed' ? 0 : undefined,
            width: '100%',
          }}
          hint={
            searchType === 'companyNumber' ? companyNumberHint : companyNameHint
          }
          // ラベルがsearchTypeのラジオボタンに反応してしまうため
          htmlFor=""
          additionalLabelComponent={
            <Radio.Group
              style={{ marginLeft: 8 }}
              value={searchType}
              onChange={(e) => {
                const value = e.target.value as SearchType;
                onReset(value);
                onChangeSearchType?.(value);
              }}
              options={[
                {
                  value: 'companyName',
                  label: t('payment.search-by-company-name'),
                },
                {
                  value: 'companyNumber',
                  label: t('payment.search-by-company-number'),
                },
              ]}
            />
          }
          {...restProps}
        >
          {searchType === 'companyName' && (
            <Button onClick={onOpenModal}>
              {isSettingChanged ? <FilterFilled /> : <FilterOutlined />}
            </Button>
          )}
        </AutoCompleteField>
      </Flex>
      {searchResult.status === 'failed' && searchResult.errorMessage && (
        <Box marginBottom={16}>
          <Text
            variant="footnote"
            color="error"
            text={searchResult.errorMessage}
          />
        </Box>
      )}
    </>
  );
};
